import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Search, Tag, Calendar, ArrowRight } from "lucide-react";
import { api } from "../lib/api";
import { Input } from "../components/ui/input";

export default function BlogListPage() {
  const [posts, setPosts] = useState([]);
  const [q, setQ] = useState("");
  const [category, setCategory] = useState("");
  const [categories, setCategories] = useState([]);

  useEffect(() => {
    document.title = "Pool Care Blog | Pool Guru Treasure Coast";
    api.get("/blog/categories").then((r) => setCategories(r.data));
  }, []);

  useEffect(() => {
    const params = new URLSearchParams();
    if (q) params.append("q", q);
    if (category) params.append("category", category);
    api.get(`/blog?${params}`).then((r) => setPosts(r.data));
  }, [q, category]);

  return (
    <div data-testid="blog-page">
      <section className="bg-gradient-to-b from-pink-50/50 to-white py-20">
        <div className="section-container">
          <div className="overline mb-3">Pool Care Blog</div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold leading-tight mb-6 max-w-4xl">
            Tips, guides & expert advice for <span className="text-brand-pink">Florida pool owners</span>.
          </h1>
          <p className="text-xl text-slate-600 max-w-3xl">
            Everything we've learned in 20+ years of servicing Treasure Coast pools — straight from our technicians to you.
          </p>
        </div>
      </section>

      <section className="py-12">
        <div className="section-container">
          <div className="flex flex-col lg:flex-row gap-4 mb-10">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Search articles..."
                value={q}
                onChange={(e) => setQ(e.target.value)}
                className="pl-10 h-12 bg-white"
                data-testid="blog-search"
              />
            </div>
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => setCategory("")}
                className={`px-4 py-2 rounded-full text-sm font-bold transition-colors ${
                  !category ? "bg-brand-pink text-white" : "bg-white border border-slate-200 text-slate-600"
                }`}
                data-testid="category-all"
              >
                All
              </button>
              {categories.map((c) => (
                <button
                  key={c}
                  onClick={() => setCategory(c)}
                  className={`px-4 py-2 rounded-full text-sm font-bold transition-colors ${
                    category === c ? "bg-brand-pink text-white" : "bg-white border border-slate-200 text-slate-600 hover:border-brand-pink"
                  }`}
                  data-testid={`category-${c.toLowerCase().replace(/\s+/g, "-")}`}
                >
                  {c}
                </button>
              ))}
            </div>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {posts.map((post) => (
              <Link
                key={post.slug}
                to={`/blog/${post.slug}`}
                className="group bg-white rounded-2xl border border-slate-200 overflow-hidden hover:-translate-y-1 hover:shadow-xl transition-all"
                data-testid={`blog-card-${post.slug}`}
              >
                {post.featured_image && (
                  <div className="aspect-[16/10] overflow-hidden">
                    <img
                      src={post.featured_image}
                      alt={post.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                )}
                <div className="p-6">
                  <div className="flex items-center gap-2 text-xs text-slate-500 mb-3">
                    <span className="px-2 py-0.5 bg-pink-50 text-brand-pink font-bold rounded">
                      {post.category}
                    </span>
                    <Calendar className="w-3 h-3" />
                    <span>{new Date(post.created_at).toLocaleDateString()}</span>
                  </div>
                  <h3 className="text-xl font-bold mb-2 group-hover:text-brand-pink transition-colors line-clamp-2">
                    {post.title}
                  </h3>
                  <p className="text-sm text-slate-600 line-clamp-2 mb-3">{post.excerpt}</p>
                  <span className="text-brand-blue font-semibold text-sm inline-flex items-center gap-1 group-hover:gap-2 transition-all">
                    Read more <ArrowRight className="w-4 h-4" />
                  </span>
                </div>
              </Link>
            ))}
          </div>

          {posts.length === 0 && (
            <div className="text-center py-20 text-slate-500">
              No articles found. Try adjusting your search.
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
